import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useMemo, useState } from 'react';

import { type CampaignActivityDataType, useCampaignActivity } from '@/api/campaign';
import { TRAFFIC_TYPES } from '@/api/common';
import { Toggle } from '@/components/buttons';
import { RangeFilter } from '@/components/filters';
import {
  type InstantSearchDataTableColumnDef,
  useInstantSearchState,
} from '@/components/InstantSearch';
import { getSolQueryParamsNewFromQueryState } from '@/components/InstantSearch/util/search-util';
import { usePageFilterContext } from '@/components/page';
import { Link, SupportLink } from '@/components/typography';
import {
  AUDIENCE_PRIMARY,
  BOT_PRIMARY,
  CAMPAIGN_GROUP_MAX_LIMIT,
  CAMPAIGN_SCORE_MAX_LIMIT,
  TAM_PRIMARY,
  UNKNOWN_PRIMARY,
  VISITS_PRIMARY,
} from '@/constants';
import { CampaignFilter, VendorFilter } from '@/features/filters';
import { StackModeToggleButton } from '@/features/StackModeToggleButton';
import {
  ViewThroughPercentCell,
  VisitsTableBarCell,
  VIEW_THROUGH_VISITS_METRIC_FIELDS,
} from '@/features/VisitsTableBarCell';
import useMaxVisitsStats from '@/features/VisitsTableBarCell/useMaxVisitsStats';
import didClickOnStickyPopover from '@/helper/didClickOnStickyPopover';
import { numberFormat } from '@/helper/numberFormatter';
import useOnClickOutside from '@/hooks/useOnClickOutside';
import { TableState } from '@/providers/TableState';
import { ROUTES } from '@/router';
import { type WebTrafficSearchParamType } from '@/router/searchParams/webTrafficSearchParams';

import CampaignActivityTable from './CampaignActivityTable';

const DEFAULT_HIDDEN_COLUMNS = [
  'adClicks',
  'countPipelineInfluenced',
  'countClosedwonInfluenced',
  'companiesReached',
  'leadsInfluenced',
  'pipelineFirstTouchAttribution',
  'closedWonFirstTouchAttribution',
  'pipelineLastTouchAttribution',
];

type Props = {
  onScoreCampaigns: (campaignIds: string[]) => void;
};

const CampaignActivityTableContainer = ({ onScoreCampaigns }: Props) => {
  const {
    pageFilters: { startDate, endDate, audience },
  } = usePageFilterContext();
  const { queryState } = useInstantSearchState();
  const [disabledSeries, setDisabledSeries] = useState<TRAFFIC_TYPES[] | undefined>([]);
  const [selectedCampaigns, setSelectedCampaigns] = useState<CampaignActivityDataType[]>([]);
  const [visitsMode, setVisitsMode] = useState<'stack' | 'percent'>('percent');
  const [includeViewThroughVisits, setIncludeViewThroughVisits] = useState(false);
  const [hoveredRow, setHoveredRow] = useState<CampaignActivityDataType | null>(null);
  const [stickyRow, setStickyRow] = useState<{
    element: HTMLElement;
    record: CampaignActivityDataType;
  } | null>(null);

  useOnClickOutside(stickyRow?.element, () => setStickyRow(null), {
    shouldIgnoreEvent: (event) => didClickOnStickyPopover(event.target),
  });

  const {
    campaignActivity: data,
    totalResults,
    isLoading,
    error,
  } = useCampaignActivity(
    getSolQueryParamsNewFromQueryState(queryState),
    startDate,
    endDate,
    audience?.id,
  );

  const maxVisits = useMaxVisitsStats({ data, audience, disabledSeries });
  const maxVisitsWithViewThrough = useMaxVisitsStats({
    data,
    audience,
    disabledSeries,
    metricFields: VIEW_THROUGH_VISITS_METRIC_FIELDS,
    sumWithBaseMetrics: true,
  });
  const visitsBarMax = includeViewThroughVisits ? maxVisitsWithViewThrough : maxVisits;

  const isMaxCompareLimit =
    selectedCampaigns.length === Math.max(CAMPAIGN_GROUP_MAX_LIMIT, CAMPAIGN_SCORE_MAX_LIMIT);

  const sortFields = ROUTES.campaigns.searchParams['c.sortBy'].enumValues;

  const columns = useMemo<InstantSearchDataTableColumnDef<CampaignActivityDataType>[]>(
    () =>
      [
        {
          header: t`Campaign`,
          id: 'campaignId',
          enableSorting: sortFields.includes('campaign.name'),
          minSize: 350,
          flex: 1,
          instantSearch: {
            sortField: 'campaign.name',
          },
          metricSelector: false,
          filterComponent: (props) => <CampaignFilter {...props} />,
          cell: ({ row }) => (
            <Link
              className="wrap-anywhere"
              variant="caption1"
              to={ROUTES.campaignById(row.original.ad.id)}
            >
              {row.original.ad.name}
            </Link>
          ),
        },
        {
          header: t`Channel Vendor`,
          id: 'vendor.id',
          enableSorting: sortFields.includes('vendor.id'),
          minSize: 200,
          flex: 1,
          filterComponent: (props) => <VendorFilter {...props} />,
          cell: ({ row }) =>
            `${row.original.ad.vendor.name} - ${row.original.ad.vendor.channel?.name}`,
        },
        {
          header: t`Spend`,
          id: 'spend',
          accessorFn: (row) => row.spend,
          meta: { align: 'end' },
          enableSorting: sortFields.includes('spend'),
          size: 110,
          infoPopover: (
            <Trans>
              Spend is reported either through your vendor's API connection or from manually
              calculated spend entered by your team on the{' '}
              <Link variant="body2" color="static-green" to={ROUTES.configureVendors.path}>
                Vendor Spend Page
              </Link>
              .
            </Trans>
          ),
          filterComponent: (props) => <RangeFilter {...props} />,
          cell: ({ getValue }) =>
            numberFormat(getValue<number>(), {
              isCurrency: true,
              precision: 0,
            }),
        },
        {
          header: t`$/Visit`,
          id: 'spendPerTargetVisit',
          accessorFn: (row) => row.spendPerTargetVisit,
          meta: { align: 'end' },
          enableSorting: sortFields.includes('spendPerTargetVisit'),
          size: 95,
          infoPopover: (
            <Trans>
              $/Visit shows the cost per audience visit (Spend / Audience Visits). Spend is reported
              either through your vendor's API connection or from manually calculated spend entered
              by your team on the{' '}
              <Link variant="body2" color="static-green" to={ROUTES.configureVendors.path}>
                Vendor Spend Page
              </Link>
              .
            </Trans>
          ),
          filterComponent: (props) => <RangeFilter {...props} />,
          cell: ({ getValue }) =>
            numberFormat(getValue<number>(), {
              isCurrency: true,
              precision: 2,
            }),
        },
        {
          header: t`Impressions`,
          id: 'adImpressions',
          accessorFn: (row) => row.adImpressions,
          meta: { align: 'end' },
          enableSorting: sortFields.includes('adImpressions'),
          size: 120,
          infoPopover: (
            <Trans>
              Volume of impressions shared with Channel99 through the ads integration. This is total
              impressions for the time period and should match your vendors reporting.
            </Trans>
          ),
          filterComponent: (props) => <RangeFilter {...props} />,
          cell: ({ row, getValue }) =>
            +getValue<number>() > 0 ? (
              <Link
                variant="caption1"
                to={ROUTES.webTrafficActivityWithParams({
                  'w.campaignIds': [row.original.ad.id],
                  'w.metrics.impressions': '[1,]',
                })}
              >
                {numberFormat(getValue<number>())}
              </Link>
            ) : (
              0
            ),
        },
        {
          header: t`Clicks`,
          id: 'adClicks',
          accessorFn: (row) => row.adClicks,
          meta: { align: 'end' },
          enableSorting: sortFields.includes('adClicks'),
          size: 90,
          infoPopover: (
            <Trans>
              Clicks reported directly from your connected vendor. This should match what your
              vendor is reporting.
            </Trans>
          ),
          filterComponent: (props) => <RangeFilter {...props} />,
          cell: ({ getValue }) => numberFormat(getValue<number>()),
        },
        {
          header: includeViewThroughVisits ? t`Visits + View Through` : t`Visits`,
          id: 'visits',
          enableSorting: sortFields.includes('visits'),
          size: 300,
          actionContent: (
            <div className="flex items-center gap-1 py-0.5">
              <Toggle
                variant="ghost"
                size="small"
                shape="square"
                pressed={includeViewThroughVisits}
                aria-label={t`Include view-through visits in chart`}
                onPressedChange={setIncludeViewThroughVisits}
              >
                <Trans>VT</Trans>
              </Toggle>
              <StackModeToggleButton mode={visitsMode} size="small" onChange={setVisitsMode} />
            </div>
          ),
          infoPopover: (
            <Trans>
              Volume of impressions shared with Channel99 through the ads integration. This is total
              impressions for the time period and should match your vendors reporting.{' '}
              <SupportLink variant="body2" color="static-green" path="VISITS">
                More details
              </SupportLink>
            </Trans>
          ),
          filterComponent: (props) => <RangeFilter {...props} />,
          cell: ({ row }) => {
            const field = row.original;
            return (
              <VisitsTableBarCell
                data={[
                  {
                    type: audience?.isTAM ? TRAFFIC_TYPES.TAM : TRAFFIC_TYPES.AUDIENCE,
                    label: audience?.name ?? t`TAM`,
                    value: includeViewThroughVisits
                      ? row.original.targetVisits + (row.original.targetViewThroughVisits ?? 0)
                      : row.original.targetVisits,
                    color: audience?.isTAM ? TAM_PRIMARY : AUDIENCE_PRIMARY,
                    disabled: audience?.isTAM
                      ? disabledSeries?.includes(TRAFFIC_TYPES.TAM) === true
                      : disabledSeries?.includes(TRAFFIC_TYPES.AUDIENCE) === true,
                  },
                  {
                    type: TRAFFIC_TYPES.OTHER,
                    label: t`Other Companies`,
                    value: includeViewThroughVisits
                      ? row.original.otherVisits + (row.original.otherViewThroughVisits ?? 0)
                      : row.original.otherVisits,
                    color: VISITS_PRIMARY,
                    disabled: disabledSeries?.includes(TRAFFIC_TYPES.OTHER) === true,
                  },
                  {
                    type: TRAFFIC_TYPES.BOT,
                    label: t`Bot`,
                    value: includeViewThroughVisits
                      ? row.original.botVisits + (row.original.botViewThroughVisits ?? 0)
                      : row.original.botVisits,
                    color: BOT_PRIMARY,
                    disabled: disabledSeries?.includes(TRAFFIC_TYPES.BOT) === true,
                  },
                  {
                    type: TRAFFIC_TYPES.UNRESOLVED,
                    label: t`Unknown`,
                    value: includeViewThroughVisits
                      ? row.original.unresolvedVisits +
                        (row.original.unresolvedViewThroughVisits ?? 0)
                      : row.original.unresolvedVisits,
                    color: UNKNOWN_PRIMARY,
                    disabled: disabledSeries?.includes(TRAFFIC_TYPES.UNRESOLVED) === true,
                  },
                ]}
                maxVisits={visitsBarMax}
                displayMode={visitsMode}
                isOpaque={
                  (stickyRow == null && hoveredRow == null) ||
                  stickyRow?.record === field ||
                  hoveredRow === field
                }
                isTooltipOpen={hoveredRow === field || stickyRow?.record === field}
                isTooltipSticky={stickyRow?.record === field}
                tooltipTitle={row.original.ad.name}
                tooltipLink={(trafficType: TRAFFIC_TYPES) => {
                  const queryParams = {
                    'w.campaignIds': [row.original.ad.id],
                    'w.metrics.visits': '[1,]',
                  } as WebTrafficSearchParamType;

                  if (trafficType === TRAFFIC_TYPES.AUDIENCE && audience?.id) {
                    return ROUTES.webTrafficActivityWithParams({
                      ...queryParams,
                      'w.audienceIds': audience.id,
                    });
                  } else if (trafficType === TRAFFIC_TYPES.TAM) {
                    return ROUTES.webTrafficActivityWithParams({
                      ...queryParams,
                      'w.inTam': 'true',
                    });
                  } else if (trafficType === TRAFFIC_TYPES.OTHER) {
                    if (audience?.isTAM) {
                      return ROUTES.webTrafficActivityWithParams({
                        ...queryParams,
                        'w.inTam': 'false',
                      });
                    }
                    return ROUTES.webTrafficActivityWithParams({
                      ...queryParams,
                      ['w.audienceIds!']: audience?.id,
                    });
                  } else if (trafficType === TRAFFIC_TYPES.ALL) {
                    return ROUTES.webTrafficActivityWithParams(queryParams);
                  }

                  return undefined;
                }}
                onStickyTooltipClose={() => setStickyRow(null)}
              />
            );
          },
          cellProps: ({ row }) => {
            const record = row.original;
            return {
              style: { cursor: 'pointer' },
              onMouseEnter: () => setHoveredRow(record),
              onMouseLeave: () => setHoveredRow(null),
              onClick: (e) => {
                if (didClickOnStickyPopover(e.target)) {
                  return;
                }

                // clicking on the same row again will close the tooltip
                if (stickyRow?.record === record) {
                  setStickyRow(null);
                } else {
                  setStickyRow({
                    element: e.currentTarget as HTMLElement,
                    record,
                  });
                }
              },
            };
          },
        },
        {
          header: t`VT %`,
          id: 'viewThroughPercent',
          meta: { align: 'end' },
          enableSorting: sortFields.includes('viewThroughPercent'),
          size: 80,
          infoPopover: (
            <Trans>
              VT % shows the share of visits that were view-through only. A view-through visit
              happens when someone views tracked content and later visits through a different direct
              source without clicking that tracked content. This metric is calculated as Total VT
              Visits / (Total VT Visits + Total Visits).
            </Trans>
          ),
          filterComponent: (props) => <RangeFilter type="percent" {...props} />,
          cell: ({ row }) => (
            <ViewThroughPercentCell
              tooltipTitle={row.original.ad.name}
              viewThroughPercent={row.original.viewThroughPercent}
              rows={[
                {
                  label: audience?.isTAM ? t`TAM` : (audience?.name ?? t`Audience`),
                  color: audience?.isTAM ? TAM_PRIMARY : AUDIENCE_PRIMARY,
                  viewThroughVisits: row.original.targetViewThroughVisits ?? 0,
                },
                {
                  label: t`Other Companies`,
                  color: VISITS_PRIMARY,
                  viewThroughVisits: row.original.otherViewThroughVisits ?? 0,
                },
                {
                  label: t`Bot`,
                  color: BOT_PRIMARY,
                  viewThroughVisits: row.original.botViewThroughVisits ?? 0,
                },
                {
                  label: t`Unknown`,
                  color: UNKNOWN_PRIMARY,
                  viewThroughVisits: row.original.unresolvedViewThroughVisits ?? 0,
                },
              ]}
            />
          ),
        },
        {
          header: t`Visit Efficiency`,
          id: 'visitEfficiency',
          meta: { align: 'end' },
          enableSorting: sortFields.includes('visitEfficiency'),
          size: 115,
          infoPopover: (
            <Trans>
              Visit efficiency is the ratio of target audience visits to total visits (unknown
              removed) to give a view of how well a vendor/campaign attracts the right audience.{' '}
              <SupportLink variant="body2" color="static-green" path="VISIT_EFFICIENCY">
                More Details
              </SupportLink>
            </Trans>
          ),
          filterComponent: (props) => <RangeFilter type="percent" {...props} />,
          cell: ({ row }) =>
            numberFormat(row.original.visitEfficiency, {
              isPercent: true,
              precision: 1,
            }),
        },
        {
          header: t`View-Through Visits`,
          id: 'viewThroughVisits',
          meta: { align: 'end' },
          enableSorting: sortFields.includes('viewThroughVisits'),
          size: 140,
          infoPopover: (
            <Trans>
              A view-through visit occurs when a user views an ad or piece of content and does not
              click through but comes to the website directly through a vendor/campaign.{' '}
              <SupportLink variant="body2" color="static-green" path="VIEW_THROUGH_VISITS">
                More Details
              </SupportLink>
            </Trans>
          ),
          filterComponent: (props) => <RangeFilter {...props} />,
          cell: ({ row }) => numberFormat(row.original.viewThroughVisits),
        },
        {
          header: t`Engaged Companies`,
          id: 'engagedCompanies',
          accessorFn: (row) => row.engagedCompanies,
          meta: { align: 'end' },
          enableSorting: sortFields.includes('engagedCompanies'),
          size: 115,
          infoPopover: (
            <Trans>Engaged companies are a count of companies which had at least one visit.</Trans>
          ),
          filterComponent: (props) => <RangeFilter {...props} />,
          cell: ({ row, getValue }) =>
            +getValue<number>() > 0 ? (
              <Link
                variant="caption1"
                to={ROUTES.webTrafficActivityWithParams({
                  'w.campaignIds': row.original.ad.id,
                  'w.audienceIds': audience?.isTAM ? undefined : audience?.id,
                  'w.inTam': audience?.isTAM ? 'true' : undefined,
                  'w.metrics.visits': '[1,]',
                })}
              >
                {numberFormat(getValue<number>())}
              </Link>
            ) : (
              0
            ),
        },
        {
          header: t`Companies Reached`,
          id: 'companiesReached',
          accessorFn: (row) => row.companiesReached,
          size: 115,
          meta: { align: 'end' },
          enableSorting: sortFields.includes('companiesReached'),
          infoPopover: (
            <Trans>
              The total number of unique companies reached within the selected time period. This can
              be from integration impressions (currently only LinkedIn) or from Pixel impressions.
            </Trans>
          ),
          filterComponent: (props) => <RangeFilter {...props} />,
          cell: ({ getValue }) => numberFormat(getValue<number>()),
        },
        {
          header: t`Leads Influence`,
          id: 'leadsInfluenced',
          accessorFn: (row) => row.leadsInfluenced,
          size: 115,
          meta: { align: 'end' },
          enableSorting: sortFields.includes('leadsInfluenced'),
          infoPopover: (
            <Trans>
              Lead influence is given to each vendor or campaign with at least one visit during the
              opportunity influence window chosen before the opportunity create date.
            </Trans>
          ),
          filterComponent: (props) => <RangeFilter {...props} />,
          cell: ({ getValue }) => numberFormat(getValue<number>()),
        },
        {
          header: t`Pipeline Influence`,
          id: 'pipelineInfluenced',
          accessorFn: (row) => row.pipelineInfluenced,
          meta: { align: 'end' },
          enableSorting: sortFields.includes('pipelineInfluenced'),
          size: 120,
          infoPopover: (
            <Trans>
              Pipeline influence is given to each vendor or campaign with at least one visit during
              the date range chosen before the opportunity create date.{' '}
              <SupportLink variant="body2" color="static-green" path="PIPELINE_INFLUENCE">
                More Details
              </SupportLink>
            </Trans>
          ),
          filterComponent: (props) => <RangeFilter {...props} />,
          cell: ({ row, getValue }) =>
            getValue<number>() === 0 ? (
              numberFormat(getValue<number>(), {
                isCurrency: true,
                precision: 0,
              })
            ) : (
              <Link
                variant="caption1"
                to={ROUTES.opportunityActivityWithParams({
                  'o.campaignIdListPipelineInfluence': row.original.ad.id,
                })}
              >
                {numberFormat(getValue<number>(), {
                  isCurrency: true,
                  precision: 0,
                })}
              </Link>
            ),
        },
        {
          header: t`# Pipeline Influenced`,
          id: 'countPipelineInfluenced',
          accessorFn: (row) => row.countPipelineInfluenced,
          meta: { align: 'end' },
          enableSorting: sortFields.includes('countPipelineInfluenced'),
          size: 145,
          infoPopover: (
            <Trans>
              Number of opportunities with pipeline influence attributed to this vendor/campaign.
            </Trans>
          ),
          filterComponent: (props) => <RangeFilter {...props} />,
          cell: ({ row, getValue }) =>
            getValue<number>() === 0 ? (
              numberFormat(getValue<number>())
            ) : (
              <Link
                variant="caption1"
                to={ROUTES.opportunityActivityWithParams({
                  'o.campaignIdListPipelineInfluence': row.original.ad.id,
                })}
              >
                {numberFormat(getValue<number>())}
              </Link>
            ),
        },
        {
          header: t`Closed Won Influence`,
          id: 'closedWonInfluenced',
          accessorFn: (row) => row.closedWonInfluenced,
          meta: { align: 'end' },
          enableSorting: sortFields.includes('closedWonInfluenced'),
          size: 120,
          infoPopover: (
            <Trans>
              Closed won influence is given to each vendor or campaign with at least one visit
              during the date range chosen and within the timeline of a closed won opportunity.{' '}
              <SupportLink variant="body2" color="static-green" path="CLOSED_WON_INFLUENCE">
                More Details
              </SupportLink>
            </Trans>
          ),
          filterComponent: (props) => <RangeFilter {...props} />,
          cell: ({ row, getValue }) =>
            getValue<number>() === 0 ? (
              numberFormat(getValue<number>(), {
                isCurrency: true,
                precision: 0,
              })
            ) : (
              <Link
                variant="caption1"
                to={ROUTES.opportunityActivityWithParams({
                  'o.campaignIdListClosedwonInfluence': row.original.ad.id,
                })}
              >
                {numberFormat(getValue<number>(), {
                  isCurrency: true,
                  precision: 0,
                })}
              </Link>
            ),
        },
        {
          header: t`# Closed Won Influenced`,
          id: 'countClosedwonInfluenced',
          accessorFn: (row) => row.countClosedwonInfluenced,
          meta: { align: 'end' },
          enableSorting: sortFields.includes('countClosedwonInfluenced'),
          size: 160,
          infoPopover: (
            <Trans>
              Number of closed-won opportunities with influence attributed to this vendor/campaign.
            </Trans>
          ),
          filterComponent: (props) => <RangeFilter {...props} />,
          cell: ({ row, getValue }) =>
            getValue<number>() === 0 ? (
              numberFormat(getValue<number>())
            ) : (
              <Link
                variant="caption1"
                to={ROUTES.opportunityActivityWithParams({
                  'o.campaignIdListClosedwonInfluence': row.original.ad.id,
                })}
              >
                {numberFormat(getValue<number>())}
              </Link>
            ),
        },
        {
          header: t`Linear Pipeline`,
          id: 'pipelineLinearAttribution',
          accessorFn: (row) => row.pipelineLinearAttribution,
          size: 120,
          meta: { align: 'end' },
          enableSorting: sortFields.includes('pipelineLinearAttribution'),
          infoPopover: (
            <Trans>
              Linear pipeline accounts for all interactions within the influence window until the
              opportunity creation date and divides the pipeline value equally to each interaction.{' '}
              <SupportLink variant="body2" color="static-green" path="PIPELINE_LINEAR_ATTRIBUTION">
                More Details
              </SupportLink>
            </Trans>
          ),
          filterComponent: (props) => <RangeFilter {...props} />,
          cell: ({ row, getValue }) =>
            getValue<number>() === 0 ? (
              numberFormat(getValue<number>(), {
                isCurrency: true,
                precision: 0,
              })
            ) : (
              <Link
                variant="caption1"
                to={ROUTES.opportunityActivityWithParams({
                  'o.campaignIdListPipelineLinear': row.original.ad.id,
                })}
              >
                {numberFormat(getValue<number>(), {
                  isCurrency: true,
                  precision: 0,
                })}
              </Link>
            ),
        },
        {
          header: t`Linear Closed Won`,
          id: 'closedWonLinearAttribution',
          accessorFn: (row) => row.closedWonLinearAttribution,
          size: 120,
          meta: { align: 'end' },
          enableSorting: sortFields.includes('closedWonLinearAttribution'),
          infoPopover: (
            <Trans>
              Linear closed won accounts for all interactions within the influence window through
              the opportunity close date and divides the pipeline value equally to each interaction.{' '}
              <SupportLink
                variant="body2"
                color="static-green"
                path="CLOSED_WON_LINEAR_ATTRIBUTION"
              >
                More Details
              </SupportLink>
            </Trans>
          ),
          filterComponent: (props) => <RangeFilter {...props} />,
          cell: ({ row, getValue }) =>
            getValue<number>() === 0 ? (
              numberFormat(getValue<number>(), {
                isCurrency: true,
                precision: 0,
              })
            ) : (
              <Link
                variant="caption1"
                to={ROUTES.opportunityActivityWithParams({
                  'o.campaignIdListClosedwonLinear': row.original.ad.id,
                })}
              >
                {numberFormat(getValue<number>(), {
                  isCurrency: true,
                  precision: 0,
                })}
              </Link>
            ),
        },
        {
          header: t`First-Touch Pipeline`,
          id: 'pipelineFirstTouchAttribution',
          accessorFn: (row) => row.pipelineFirstTouchAttribution,
          size: 120,
          meta: { align: 'end' },
          enableSorting: sortFields.includes('pipelineFirstTouchAttribution'),
          infoPopover: (
            <Trans>
              The first vendor/campaign which was interacted with (visit or non-digital event) from
              the beginning of the influence window to the opportunity create date.{' '}
              <SupportLink
                variant="body2"
                color="static-green"
                path="PIPELINE_FIRST_TOUCH_ATTRIBUTION"
              >
                More Details
              </SupportLink>
            </Trans>
          ),
          filterComponent: (props) => <RangeFilter {...props} />,
          cell: ({ row, getValue }) =>
            getValue<number>() === 0 ? (
              numberFormat(getValue<number>(), {
                isCurrency: true,
                precision: 0,
              })
            ) : (
              <Link
                variant="caption1"
                to={ROUTES.opportunityActivityWithParams({
                  'o.campaignIdListPipelineFirstTouch': row.original.ad.id,
                })}
              >
                {numberFormat(getValue<number>(), {
                  isCurrency: true,
                  precision: 0,
                })}
              </Link>
            ),
        },
        {
          header: t`First-Touch Closed Won`,
          id: 'closedWonFirstTouchAttribution',
          accessorFn: (row) => row.closedWonFirstTouchAttribution,
          size: 120,
          meta: { align: 'end' },
          enableSorting: sortFields.includes('closedWonFirstTouchAttribution'),
          infoPopover: (
            <Trans>
              The first vendor/campaign which was interacted with (visit or non-digital event) from
              the beginning of the influence window to the opportunity close date.{' '}
              <SupportLink
                variant="body2"
                color="static-green"
                path="CLOSED_WON_FIRST_TOUCH_ATTRIBUTION"
              >
                More Details
              </SupportLink>
            </Trans>
          ),
          filterComponent: (props) => <RangeFilter {...props} />,
          cell: ({ row, getValue }) =>
            getValue<number>() === 0 ? (
              numberFormat(getValue<number>(), {
                isCurrency: true,
                precision: 0,
              })
            ) : (
              <Link
                variant="caption1"
                to={ROUTES.opportunityActivityWithParams({
                  'o.campaignIdListClosedwonFirstTouch': row.original.ad.id,
                })}
              >
                {numberFormat(getValue<number>(), {
                  isCurrency: true,
                  precision: 0,
                })}
              </Link>
            ),
        },
        {
          header: t`Last-Touch Pipeline`,
          id: 'pipelineLastTouchAttribution',
          accessorFn: (row) => row.pipelineLastTouchAttribution,
          size: 120,
          meta: { align: 'end' },
          enableSorting: sortFields.includes('pipelineLastTouchAttribution'),
          infoPopover: (
            <Trans>
              The vendor/campaign with the previous interaction to the create date of the
              opportunity.{' '}
              <SupportLink
                variant="body2"
                color="static-green"
                path="PIPELINE_LAST_TOUCH_ATTRIBUTION"
              >
                More Details
              </SupportLink>
            </Trans>
          ),
          filterComponent: (props) => <RangeFilter {...props} />,
          cell: ({ row, getValue }) =>
            getValue<number>() === 0 ? (
              numberFormat(getValue<number>(), {
                isCurrency: true,
                precision: 0,
              })
            ) : (
              <Link
                variant="caption1"
                to={ROUTES.opportunityActivityWithParams({
                  'o.campaignIdListPipelineLastTouch': row.original.ad.id,
                })}
              >
                {numberFormat(getValue<number>(), {
                  isCurrency: true,
                  precision: 0,
                })}
              </Link>
            ),
        },
      ] satisfies InstantSearchDataTableColumnDef<CampaignActivityDataType>[],
    [
      hoveredRow,
      stickyRow,
      disabledSeries,
      includeViewThroughVisits,
      visitsMode,
      visitsBarMax,
      setVisitsMode,
      setIncludeViewThroughVisits,
      audience,
      sortFields,
    ],
  );

  return (
    <TableState
      tableKey="campaignActivity"
      columns={columns}
      defaultHiddenColumns={DEFAULT_HIDDEN_COLUMNS}
    >
      <CampaignActivityTable
        columns={columns}
        selectedCampaigns={selectedCampaigns}
        isMaxCompareLimit={isMaxCompareLimit}
        data={data}
        totalResults={totalResults}
        isLoading={isLoading}
        error={error}
        onScoreCampaigns={onScoreCampaigns}
        onDisabledSeriesChange={setDisabledSeries}
        onSelectedCampaignsChange={setSelectedCampaigns}
      />
    </TableState>
  );
};

export default CampaignActivityTableContainer;
